/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import { Refresh, Cached } from "@mui/icons-material";
import ProductCard from "../../components/common/ProductCard";
import { ProductFilters } from "../../components/filters";
import useServerFilters from "../../hooks/useServerFilters";
import useInfiniteScrollReal from "../../hooks/useInfiniteScrollReal"; // Hook original de scroll infinito
import ScrollToTopButton from "../../components/common/ScrollToTopButton";
import Layout from "../../components/layout/Layout";
import { useSEO } from "../../hooks/useSEO";

const ProductCatalog = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // 🔧 CORREGIDO: Primero declarar hook de filtros
  const {
    filters, // 🔧 Ahora filters ya contiene stableFilters internamente
    tempFilters,
    hasUnappliedChanges,
    loading: filtersLoading,
    updateFilters,
    updateTempFilters,
    applyFilters,
    resetTempFilters,
    clearAllFilters,
    activeFiltersCount,
    categories,
    brands, // ✅ CORREGIDO: usar brands del hook
    models, // ✅ CORREGIDO: usar models del hook (antes era modelos)
    ubicaciones,
    conditions,
    allFeatures,
    priceRanges,
    formatPrice,
    retry: retryFilters,
    error: filtersError,
    getApiParams, // Agregar esta función
    filterOptions, // Agregar filterOptions
    rawFilters, // 🔧 NUEVO: Para debugging si se necesita
  } = useServerFilters({}, []); // ✅ INICIALMENTE SIN PRODUCTOS

  // Hook para scroll infinito tradicional - USAR filters y getApiParams
  const {
    products,
    totalProducts,
    loading: productsLoading,
    initialLoading,
    hasMore,
    error: productsError,
    saveScrollPosition,
    containerRef,
    stats,
  } = useInfiniteScrollReal(filters, 10, getApiParams); // 🔧 Usar filters directamente

  // SEO dinámico basado en filtros activos
  const generateSEOData = useMemo(() => {
    let title = "Catálogo de Productos";
    let description =
      "Explora nuestro catálogo completo de vehículos, equipos y maquinaria usada. Encuentra exactamente lo que necesitas con nuestros filtros avanzados.";
    let keywords =
      "catálogo productos usados, vehículos usados, maquinaria usada, equipos usados";

    // Personalizar SEO basado en filtros activos
    if (activeFiltersCount > 0) {
      const activeFiltersText = [];

      if (filters.category) {
        activeFiltersText.push(filters.category);
        title = `${filters.category} - ${title}`;
        keywords = `${filters.category.toLowerCase()}, ${keywords}`;
      }

      if (filters.brand) {
        activeFiltersText.push(filters.brand);
        title = `${filters.brand} - ${title}`;
        keywords = `${filters.brand.toLowerCase()}, ${keywords}`;
      }

      if (filters.ubicacion) {
        activeFiltersText.push(`en ${filters.ubicacion}`);
        keywords = `${filters.ubicacion.toLowerCase()}, ${keywords}`;
      }

      if (activeFiltersText.length > 0) {
        description = `Encuentra ${activeFiltersText.join(
          " ",
        )} en nuestro catálogo. ${description}`;
      }
    }

    if (totalProducts > 0) {
      description += ` Mostrando ${
        products?.length || 0
      } de ${totalProducts} productos disponibles.`;
    }

    return { title, description, keywords };
  }, [filters, activeFiltersCount, totalProducts, products?.length]);

  useSEO({
    title: generateSEOData.title,
    description: generateSEOData.description,
    keywords: generateSEOData.keywords,
    image: "/og-catalog.jpg",
    type: "website",
  });

  useEffect(() => {
    console.log("📊 ProductCatalog with Infinite Scroll:", {
      products,
      filtersLoading,
      productsLoading,
      initialLoading,
      productsDisplayed: products?.length || 0, // Safe check for undefined products
      totalProducts,
      hasMore,
      activeFiltersCount,
      currentFilters: filters, // Agregar para debugging
      filterOptions: {
        categories: categories.length,
        models: models.length, // Cambiar brands a models y marcas a modelos
        locations: ubicaciones.length,
      },
      stats,
    });
  }, [products]);

  // 🔍 NUEVO LOG: Detectar cuando cambian los filtros en ProductCatalog
  useEffect(() => {
    console.log("🏷️ ProductCatalog - FILTERS CHANGED:", {
      filters,
      activeFiltersCount,
      timestamp: Date.now(),
      stringifiedFilters: JSON.stringify(filters, null, 2),
    });
  }, [filters, activeFiltersCount]);

  // Componente de información de filtros
  const FiltersInfoComponent = () => (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity={filtersLoading ? "info" : "success"}
        sx={{ mb: 2 }}
        icon={<Cached />}
      >
        <Typography variant="body2">
          <strong>Filtros:</strong>{" "}
          {filtersLoading ? "Cargando opciones..." : "✅ Opciones cargadas"} •{" "}
          {categories.length} categorías • {models.length} modelos •{" "}
          {ubicaciones.length} ubicaciones
          {activeFiltersCount > 0 && ` • ${activeFiltersCount} filtros activos`}
        </Typography>
      </Alert>
    </Box>
  );

  // Calcular productos a mostrar
  const productsToShow = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];

    // ✅ NUEVO: Detectar productos duplicados
    const productIds = new Map();
    const duplicates = [];

    products.forEach((product, index) => {
      if (productIds.has(product.id)) {
        const firstOccurrence = productIds.get(product.id);
        duplicates.push({
          id: product.id,
          name: product.name,
          firstIndex: firstOccurrence.index,
          duplicateIndex: index,
          firstProduct: firstOccurrence.product,
          duplicateProduct: product,
        });

        console.error("🚨 PRODUCTO DUPLICADO DETECTADO:", {
          id: product.id,
          name: product.name,
          firstOccurrence: {
            index: firstOccurrence.index,
            product: firstOccurrence.product,
          },
          duplicate: {
            index: index,
            product: product,
          },
          totalProducts: products.length,
          timestamp: Date.now(),
        });
      } else {
        productIds.set(product.id, { index, product });
      }
    });

    if (duplicates.length > 0) {
      console.error("🚨 RESUMEN DE DUPLICADOS:", {
        totalDuplicates: duplicates.length,
        totalProducts: products.length,
        uniqueProducts: productIds.size,
        duplicates: duplicates,
        currentFilters: filters,
        timestamp: Date.now(),
      });
    }

    return products;
  }, [products, filters]);

  // Mostrar loading inicial solo si no hay productos y está cargando
  if (initialLoading && (!products || products.length === 0)) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography
          variant="h3"
          fontWeight={"bold"}
          textAlign={"center"}
          component="h1"
          gutterBottom
        >
          Catálogo de Productos
        </Typography>

        {/* Información del estado de filtros */}
        {/* <FiltersInfoComponent /> */}

        {/* Debug temporal para limpiar localStorage corrupto */}
        {import.meta.env.DEV && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => {
                localStorage.removeItem("products-state");
                localStorage.removeItem("filters-state");
                localStorage.removeItem("scroll-position");
                sessionStorage.clear();
                window.location.reload();
              }}
              sx={{ mr: 2 }}
            >
              🧹 Limpiar Todo y Recargar
            </Button>
            <Typography variant="caption" color="text.secondary">
              Products: {products.length} • Page: {stats.currentPage} • Cache:{" "}
              {stats.cacheSize}
            </Typography>
          </Box>
        )}

        {/* Manejo de errores */}
        {(filtersError || productsError) && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                {filtersError && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={retryFilters}
                    startIcon={<Refresh />}
                  >
                    Recargar Filtros
                  </Button>
                )}
              </Box>
            }
          >
            {filtersError && `Error en filtros: ${filtersError}`}
            {productsError && `Error en productos: ${productsError}`}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Filtros - Desktop: Sidebar, Mobile: Collapsible */}
          <Grid size={{ xs: 12, md: 3 }}>
            <ProductFilters
              filters={filters}
              tempFilters={tempFilters}
              hasUnappliedChanges={hasUnappliedChanges}
              updateFilters={updateFilters}
              updateTempFilters={updateTempFilters}
              applyFilters={applyFilters}
              resetTempFilters={resetTempFilters}
              clearAllFilters={clearAllFilters}
              activeFiltersCount={activeFiltersCount}
              categories={categories}
              brands={brands} // ✅ NUEVO: Array de marcas
              models={models} // ✅ ACTUALIZADO: Array de modelos
              ubicaciones={ubicaciones}
              conditions={conditions}
              allFeatures={allFeatures}
              priceRanges={priceRanges}
              formatPrice={formatPrice}
              filterOptions={filterOptions}
              isMobile={isMobile}
              loading={filtersLoading}
            />
          </Grid>

          {/* Contenido principal */}
          <Grid size={{ xs: 12, md: 9 }}>
            {/* Contador y estado de carga */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  component="div"
                >
                  {initialLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      Cargando productos...
                    </Box>
                  ) : (
                    <>
                      Mostrando {products.length}{" "}
                      {totalProducts > 0 && `de ${totalProducts}`} productos
                      {activeFiltersCount > 0 &&
                        ` • ${activeFiltersCount} filtros activos`}
                      {" • Scroll infinito"}
                    </>
                  )}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {!initialLoading && hasMore && (
                    <Typography variant="caption" color="text.secondary">
                      {productsLoading ? "Cargando más..." : "Scroll para más"}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Grid de productos con scroll infinito */}
            {initialLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                {/* Grid de productos */}
                <Grid container spacing={3}>
                  {products && products.length > 0 ? (
                    products.map((product) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                        <ProductCard
                          product={product}
                          onCardClick={() => saveScrollPosition()}
                        />
                      </Grid>
                    ))
                  ) : (
                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 8,
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          No se encontraron productos
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {activeFiltersCount > 0
                            ? "Prueba ajustando los filtros o eliminando algunos criterios"
                            : "No hay productos disponibles"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          {activeFiltersCount > 0 && (
                            <Button
                              variant="outlined"
                              onClick={clearAllFilters}
                            >
                              Limpiar todos los filtros
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Sentinela para scroll infinito */}
                {hasMore && (
                  <Box
                    id="scroll-sentinel"
                    sx={{
                      height: "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 4,
                    }}
                  >
                    {productsLoading ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Cargando más productos...
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        ⬇️ Scroll para cargar más productos
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Mensaje de final */}
                {!hasMore && products.length > 0 && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      ✅ Has visto todos los productos disponibles
                    </Typography>
                  </Box>
                )}

                {/* Stats de scroll infinito para debugging */}
                {import.meta.env.DEV && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      📊 Scroll Infinito: Mostrando {products.length} productos
                      • Total: {totalProducts} • Página: {stats.currentPage} •
                      Cache: {stats.cacheSize} páginas • Estado:{" "}
                      {stats.loading ? "Cargando" : "Listo"} • Filtros activos:{" "}
                      {activeFiltersCount}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>

        {/* Botón para volver arriba */}
        <ScrollToTopButton />
      </Container>
    );
  }

  // Si no está en loading inicial, renderizar normalmente
  return (
    <Container maxWidth="xl" sx={{ py: 4 }} ref={containerRef}>
      <Typography
        variant="h3"
        fontWeight={"bold"}
        textAlign={"center"}
        component="h1"
        gutterBottom
      >
        Catálogo de Productos
      </Typography>

      {/* Información del estado de filtros */}
      {/* <FiltersInfoComponent /> */}

      {/* Debug temporal para limpiar localStorage corrupto */}
      {import.meta.env.DEV && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => {
              localStorage.removeItem("products-state");
              localStorage.removeItem("filters-state");
              localStorage.removeItem("scroll-position");
              sessionStorage.clear();
              window.location.reload();
            }}
            sx={{ mr: 2 }}
          >
            🧹 Limpiar Todo y Recargar
          </Button>
          <Typography variant="caption" color="text.secondary">
            Products: {productsToShow.length} • Page: {stats.currentPage} •
            Cache: {stats.cacheSize}
          </Typography>
        </Box>
      )}

      {/* Manejo de errores */}
      {(filtersError || productsError) && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              {filtersError && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={retryFilters}
                  startIcon={<Refresh />}
                >
                  Recargar Filtros
                </Button>
              )}
            </Box>
          }
        >
          {filtersError && `Error en filtros: ${filtersError}`}
          {productsError && `Error en productos: ${productsError}`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Filtros - Desktop: Sidebar, Mobile: Collapsible */}
        <Grid size={{ xs: 12, md: 3 }}>
          <ProductFilters
            filters={filters}
            tempFilters={tempFilters}
            hasUnappliedChanges={hasUnappliedChanges}
            updateFilters={updateFilters}
            updateTempFilters={updateTempFilters}
            applyFilters={applyFilters}
            resetTempFilters={resetTempFilters}
            clearAllFilters={clearAllFilters}
            activeFiltersCount={activeFiltersCount}
            categories={categories}
            brands={brands} // ✅ NUEVO: Array de marcas
            models={models} // ✅ ACTUALIZADO: Array de modelos
            ubicaciones={ubicaciones}
            conditions={conditions}
            allFeatures={allFeatures}
            priceRanges={priceRanges}
            formatPrice={formatPrice}
            filterOptions={filterOptions}
            isMobile={isMobile}
            loading={filtersLoading}
          />
        </Grid>

        {/* Contenido principal */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Contador y estado de carga */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                component="div"
              >
                {initialLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    Cargando productos...
                  </Box>
                ) : (
                  <>
                    Mostrando {productsToShow.length}{" "}
                    {totalProducts > 0 && `de ${totalProducts}`} productos
                    {activeFiltersCount > 0 &&
                      ` • ${activeFiltersCount} filtros activos`}
                  </>
                )}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {!initialLoading && hasMore && (
                  <Typography variant="caption" color="text.secondary">
                    {productsLoading ? "Cargando más..." : "Scroll para más"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Grid de productos con scroll infinito */}
          <>
            {/* Grid de productos */}
            <Grid container spacing={3}>
              {productsToShow.length > 0 ? (
                productsToShow.map((product) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                    <ProductCard
                      product={product}
                      onCardClick={() => saveScrollPosition()}
                    />
                  </Grid>
                ))
              ) : (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      No se encontraron productos
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {activeFiltersCount > 0
                        ? "Prueba ajustando los filtros o eliminando algunos criterios"
                        : "No hay productos disponibles"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {activeFiltersCount > 0 && (
                        <Button variant="outlined" onClick={clearAllFilters}>
                          Limpiar todos los filtros
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* Sentinela para scroll infinito */}
            {hasMore && (
              <Box
                id="scroll-sentinel"
                sx={{
                  height: "100px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 4,
                }}
              >
                {productsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando más productos...
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    ⬇️ Scroll para cargar más productos
                  </Typography>
                )}
              </Box>
            )}

            {/* Mensaje de final */}
            {!hasMore && productsToShow.length > 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  ✅ Has visto todos los productos disponibles
                </Typography>
              </Box>
            )}

            {/* Stats de scroll infinito para debugging */}
            {import.meta.env.DEV && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  📊 Scroll Infinito: Mostrando {productsToShow.length}{" "}
                  productos • Total: {totalProducts} • Página:{" "}
                  {stats.currentPage} • Cache: {stats.cacheSize} páginas •
                  Estado: {stats.loading ? "Cargando" : "Listo"} • Filtros
                  activos: {activeFiltersCount}
                </Typography>
              </Box>
            )}
          </>
        </Grid>
      </Grid>

      {/* Botón para volver arriba */}
      <ScrollToTopButton />
    </Container>
  );
};

export default ProductCatalog;
